import { useState } from "react";
import {
  AppBar,
  Avatar,
  Box,
  Button,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Toolbar,
} from "@mui/material";

import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { isAllowed } from "authorization/authorization.util";
import { LinkMenu, MenuButtonProps } from "components/LinkMenu/LinkMenu";
import { IUser } from "interfaces/user.interface";
import { clearCookies, getLogoutUrl } from "utils/auth.util";

import { AdminMenu } from "./AdminMenu";
import { ThemeSwitch } from "./ThemeSwitch";

import adminRoutes from "../adminRoutes";

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: 32,
  height: 32,
  fontSize: 16,
  color: theme.vars.palette.text.primary,
  backgroundColor: theme.vars.palette.backgrounds.neutral.strong.default,
}));

const StyledAppBar = styled(AppBar)(({ theme }) => {
  return {
    background: theme.vars.palette.background.paper,
    color: theme.vars.palette.text.primary,
    height: 55,
    minHeight: 55,
    boxShadow: "none",
    paddingRight: 16,

    borderBottom: `1px solid ${theme.vars.palette.divider}`,
    "& .MuiToolbar-root": {
      minHeight: 55,
      paddingLeft: 0,
      paddingRight: 0,
      maxWidth: "100%",
    },
  };
});

const UserNameWrapped = styled("div")(() => {
  return {
    maxWidth: 150,
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    float: "right",
    fontSize: 16,
    marginLeft: 8,
    display: "block",
  };
});

function UserMenuButton({ onClick, children }: MenuButtonProps) {
  return (
    <IconButton onClick={onClick}>
      <StyledAvatar>{children}</StyledAvatar>
    </IconButton>
  );
}

type AdminAppBarProps = {
  authUser: Pick<IUser, "first_name" | "last_name" | "roles"> | undefined;
};

export function AdminAppBar({ authUser }: AdminAppBarProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [menuOpen, setMenuOpen] = useState(false);

  const profileLinks = [
    {
      name: t("employee_profile"),
      href: "/profile",
      current: location.pathname.includes("/profile"),
    },
    {
      name: t("Logout"),
      onClick: () => {
        clearCookies();
        navigate(getLogoutUrl());
      },
      current: false,
    },
  ];

  const mainLinks = adminRoutes
    .filter((route) => isAllowed(authUser, route.allowedRoles))
    .filter((item) => "link" in item)
    .map((f) => ({
      name: t(f.link.name),
      href: f.link.href,
      current: location.pathname.includes(f.link.href),
    }));

  const tabIndex = mainLinks.findIndex((link) => link.current);

  return (
    <Box>
      <StyledAppBar position="fixed">
        <Toolbar
          sx={{ display: "flex", gap: 3, maxWidth: "100vw", paddingInline: 1 }}
        >
          <Box
            component={Link}
            to={mainLinks[0]?.href}
            height={"100%"}
            aria-label={mainLinks[0]?.name}
          >
            <Box
              component={"img"}
              src={"/images/vitalthings-logo.png"}
              sx={{
                height: "100%",
                padding: 1.5,
              }}
              alt="VitalThings Logo"
            />
          </Box>
          <Box
            sx={{
              flexGrow: 1,
              flexShrink: 1,
              flexBasis: 0,
              display: { xs: "none", lg: "flex" },
              height: "100%",
              paddingTop: 0.5,
            }}
          >
            <Tabs
              value={tabIndex === -1 ? false : tabIndex}
              color={"palette.textSecondary"}
              sx={{ height: "100%" }}
            >
              {mainLinks.map((link) => (
                <Tab
                  key={link.href}
                  component={Link}
                  to={link.href}
                  aria-current={link.current}
                  label={link.name}
                />
              ))}
            </Tabs>
          </Box>
          <Stack
            direction={"row"}
            alignItems={"center"}
            justifyContent={"flex-end"}
            sx={{ flexGrow: { xs: 1, lg: 0 } }}
          >
            <UserNameWrapped sx={{ display: { xs: "none", md: "initial" } }}>
              {authUser?.first_name} {authUser?.last_name}
            </UserNameWrapped>
            <LinkMenu links={profileLinks} Button={UserMenuButton}>
              {authUser?.first_name?.[0] ?? "U"}
              {authUser?.last_name?.[0] ?? ""}
            </LinkMenu>
          </Stack>
          <ThemeSwitch sx={{ display: { xs: "none", lg: "flex" } }} />
          <Button
            onClick={() => setMenuOpen(!menuOpen)}
            sx={{ display: { xs: "initial", lg: "none" } }}
          >
            {"Menu"}
          </Button>
          <AdminMenu
            isOpen={menuOpen}
            onClose={() => setMenuOpen(false)}
            links={[...mainLinks, ...profileLinks]}
          />
        </Toolbar>
      </StyledAppBar>
      <Box sx={{ minHeight: "55px" }} />
    </Box>
  );
}
